<template>
  <div class="page-wrapper">
    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">Student Practicum Attendance</h1>
        <p class="page-subtitle">Manage practicum attendance for students</p>
      </div>
      <div class="header-right">
        <v-btn color="#16a34a" class="add-btn" elevation="0" @click="addnew">
          <v-icon start size="18">mdi-plus</v-icon>
          ADD NEW
        </v-btn>
      </div>
    </div>

    <!-- ── Filter Bar ── -->
    <div class="filter-bar">
      <v-icon size="18" color="#6b7280">mdi-filter-variant</v-icon>
      <v-autocomplete
        v-model="batch_selected"
        @update:modelValue="initialize"
        :items="batchList"
        item-value="id"
        :item-title="(item) => `${item.name}  (${item.year})`"
        label="Select Batch"
        variant="outlined"
        density="compact"
        hide-details
        class="rounded-lg filter-select"
        :color="$vuetify.theme.themes.light.inputColor"
      ></v-autocomplete>
    </div>

    <!-- ── Practicum Table Card ── -->
    <div class="content-card">
      <div class="card-toolbar">
        <div class="toolbar-left">
          <span class="card-title">Practicum Attendance List</span>
          <span class="count-chip" v-if="data.length > 0">{{
            data.length
          }}</span>
        </div>
        <div class="toolbar-right">
          <v-text-field
            v-model="search"
            placeholder="Search practicum attendance..."
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            class="search-field"
            color="#16a34a"
          />
        </div>
      </div>

      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="data"
        :search="search"
        :loading="loading"
        hover
        :items-per-page="10"
        density="comfortable"
        class="skills-table"
      >
        <template v-slot:[`item.type`]="{ item }">
          <span
            class="type-badge"
            :style="{
              color: getTypeColor(item.type).text,
              backgroundColor: getTypeColor(item.type).bg,
            }"
          >
            {{ item.type }}
          </span>
        </template>

        <template v-slot:[`item.date`]="{ item }">
          <div class="cell-with-icon">
            <v-icon size="14" color="#9ca3af">mdi-calendar</v-icon>
            {{ formatDate(item.date_sched) }}
          </div>
        </template>

        <template v-slot:[`item.duration`]="{ item }">
          <div class="cell-with-icon">
            <v-icon size="14" color="#9ca3af">mdi-clock-outline</v-icon>
            {{ formatTime(item.time_from) }} - {{ formatTime(item.time_to) }}
          </div>
        </template>

        <template v-slot:[`item.venue`]="{ item }">
          <div class="cell-with-icon">
            <v-icon size="14" color="#9ca3af">mdi-map-marker</v-icon>
            {{ item.venue || "—" }}
          </div>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="action-btns">
            <template v-if="canBeEdited(item)">
              <v-btn
                class="action-btn qr-btn"
                elevation="0"
                size="small"
                @click="showQRDialog(item)"
              >
                <v-icon size="14" class="mr-1">mdi-qrcode</v-icon>
                show QR
              </v-btn>
              <v-btn
                class="action-btn edit-btn"
                elevation="0"
                size="small"
                @click="editItem(item)"
              >
                <v-icon size="14" class="mr-1">mdi-pencil</v-icon>
                EDIT
              </v-btn>
            </template>
            <span v-else class="locked-label">
              <v-icon size="14" class="mr-1">mdi-lock-outline</v-icon>
              Completed
            </span>
            <!-- <v-btn
              class="action-btn delete-btn"
              elevation="0"
              size="small"
              @click="deleteItem(item)"
            >
              <v-icon size="14" class="mr-1">mdi-delete-outline</v-icon>
              DELETE
            </v-btn> -->
          </div>
        </template>

        <template #no-data>
          <div class="empty-state">
            <v-icon size="40" color="#d1d5db">mdi-lightbulb-off-outline</v-icon>
            <p>No data found.</p>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- ── Dialogs ── -->
    <StudentPracticumAttendanceDialog :data="updateData" :action="action" />

    <v-dialog v-model="dialogConfirmDelete" max-width="420">
      <v-card class="confirm-dialog">
        <div class="dialog-header">
          <div class="dialog-icon-wrap">
            <v-icon size="22" color="#dc2626">mdi-delete-outline</v-icon>
          </div>
          <div>
            <div class="dialog-title">Delete Skill</div>
            <div class="dialog-sub">This action cannot be undone.</div>
          </div>
        </div>
        <v-divider></v-divider>
        <div class="dialog-body">
          Are you sure you want to delete this skill? It will be permanently
          removed from the system.
        </div>
        <v-divider></v-divider>
        <div class="dialog-actions">
          <v-btn
            class="dialog-cancel-btn"
            elevation="0"
            @click="dialogConfirmDelete = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="dialog-confirm-btn"
            elevation="0"
            @click="confirmDelete"
          >
            Delete
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="show_qr_dialog" max-width="800" persistent>
      <v-card flat rounded="xl" class="table-card qr-dialog-card">
        <div class="qr-dialog-header">
          <div>
            <div class="qr-dialog-title">Today's QR Codes</div>
            <div class="qr-dialog-sub">Scan to record attendance</div>
          </div>
          <v-btn
            icon="mdi-close"
            variant="text"
            color="white"
            @click="closeQRDialog()"
          ></v-btn>
        </div>

        <div
          class="d-flex align-center justify-space-between pa-4"
          v-if="qr_to_generate == null"
        >
          <div style="width: 100%">
            <v-select
              v-model="list_selected"
              :items="list"
              label="Please Select Session Time"
              variant="outlined"
              density="compact"
              hide-details
              class="rounded-lg"
              :color="$vuetify.theme.themes.light.inputColor"
              :menu-props="{ scrim: true, scrollStrategy: 'close' }"
            ></v-select>
            <div style="margin-top: 10px">
              <v-select
                v-model="type_selected"
                :items="typeList"
                label="Please Select Session Type"
                variant="outlined"
                density="compact"
                hide-details
                class="rounded-lg"
                :color="$vuetify.theme.themes.light.inputColor"
                :menu-props="{ scrim: true, scrollStrategy: 'close' }"
              ></v-select>
            </div>
          </div>
          <v-btn
            variant="tonal"
            color="#16a34a"
            rounded="lg"
            size="small"
            prepend-icon="mdi-qrcode"
            style="margin-left: 16px; flex-shrink: 0"
            @click="selectedSession"
          >
            Generate QR
          </v-btn>
        </div>
        <div class="pa-4 d-flex justify-end" v-else>
          <v-btn
            variant="tonal"
            color="#16a34a"
            rounded="lg"
            size="small"
            prepend-icon="mdi-refresh"
            @click="qr_to_generate = null"
          >
            Reset
          </v-btn>
        </div>

        <v-divider />
        <div class="pa-4 d-flex justify-center ga-4">
          <v-card
            v-if="qr_to_generate == 1"
            flat
            rounded="xl"
            style="border: 1px solid #fed7aa"
          >
            <div
              style="background-color: #fff7ed; color: #9a3412"
              class="pa-3 d-flex align-center ga-2 rounded-t-xl"
            >
              <v-icon size="18" color="#ea580c">mdi-weather-sunny</v-icon>
              <span class="font-weight-bold" style="font-size: 13px"
                >Morning Session</span
              >
            </div>
            <v-divider style="border-color: #fed7aa" />
            <div class="d-flex ga-3 pa-3">
              <div
                class="qr-box border rounded-xl text-center pa-2"
                v-if="type_selected == 'In'"
              >
                <div class="pa-3 d-flex justify-center">
                  <qrcode-vue
                    :value="qr_am_in"
                    :size="size"
                    level="H"
                    render-as="svg"
                  />
                </div>
                <div class="qr-label">Time In</div>
              </div>
              <div
                class="qr-box border rounded-xl text-center pa-2"
                v-if="type_selected == 'Out'"
              >
                <div class="pa-3 d-flex justify-center">
                  <qrcode-vue
                    :value="qr_am_out"
                    :size="size"
                    level="H"
                    render-as="svg"
                  />
                </div>
                <div class="qr-label">Time Out</div>
              </div>
            </div>
          </v-card>
          <v-card
            v-if="qr_to_generate == 2"
            flat
            rounded="xl"
            style="border: 1px solid #bfdbfe"
          >
            <div
              style="background-color: #eff6ff; color: #1e40af"
              class="pa-3 d-flex align-center ga-2 rounded-t-xl"
            >
              <v-icon size="18" color="#3b82f6">mdi-weather-sunset</v-icon>
              <span class="font-weight-bold" style="font-size: 13px"
                >Afternoon Session</span
              >
            </div>
            <v-divider style="border-color: #bfdbfe" />
            <div class="d-flex ga-3 pa-3">
              <div
                class="qr-box border rounded-xl text-center pa-2"
                v-if="type_selected == 'In'"
              >
                <div class="pa-3 d-flex justify-center">
                  <qrcode-vue
                    :value="qr_pm_in"
                    :size="size"
                    level="H"
                    render-as="svg"
                  />
                </div>
                <div class="qr-label">Time In</div>
              </div>
              <div
                class="qr-box border rounded-xl text-center pa-2"
                v-if="type_selected == 'Out'"
              >
                <div class="pa-3 d-flex justify-center">
                  <qrcode-vue
                    :value="qr_pm_out"
                    :size="size"
                    level="H"
                    render-as="svg"
                  />
                </div>
                <div class="qr-label">Time Out</div>
              </div>
            </div>
          </v-card>
        </div>
      </v-card>
    </v-dialog>

    <fade-away-message-component
      :displayType="'variation2'"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :type="fadeAwayMessage.type"
    />
  </div>
</template>

<script>
import QrcodeVue from "qrcode.vue";
import eventBus from "@/eventBus";
import StudentPracticumAttendanceDialog from "../Dialogs/Forms/StudentPracticumAttendanceDialog.vue";
export default {
  components: { StudentPracticumAttendanceDialog, QrcodeVue },

  data: () => ({
    search: "",
    headers: [
      { title: "Type", value: "type", align: "start" },
      { title: "Title", value: "title", align: "start" },
      { title: "Date", value: "date", align: "start" },
      { title: "Duration", value: "duration", align: "start" },
      { title: "Venue", value: "venue", align: "start" },
      {
        title: "Actions",
        value: "actions",
        align: "end",
        sortable: false,
        width: 200,
      },
    ],
    list: ["Morning Session", "Afternoon Session"],
    typeList: ["In", "Out"],
    list_selected: "Morning Session",
    type_selected: "In",
    data: [],
    perPageChoices: [
      { text: "5", value: 5 },
      { text: "10", value: 10 },
      { text: "20", value: 20 },
      { text: "50", value: 50 },
      { text: "100", value: 100 },
      { text: "250", value: 250 },
      { text: "500", value: 500 },
    ],
    batch_selected: null,
    batchList: [],
    totalCount: 0,
    deleteData: null,
    updateData: null,
    alerted: false,
    loading: false,
    options: {},
    action: null,
    paginationData: {},
    formdata: [],
    work_dates_menu: false,
    dialogConfirmDelete: false,
    fadeAwayMessage: {
      show: false,
      type: "success",
      header: "Successfully Deleted!",
      message: "",
      top: 10,
    },
    qr_to_generate: null,
    show_qr_dialog: false,
    itemToAttendance: null,
    size: 300,
  }),
  mounted() {
    this.getBatchList();
    this.initialize();
    eventBus.on("closeStudentPracticumAttendanceDialog", () => {
      this.initialize();
    });
  },
  beforeUnmount() {
    eventBus.off("closeStudentPracticumAttendanceDialog");
  },
  computed: {
    qr_am_in() {
      const payload = {
        type: "am_in",
        org_id: null,
        student_practicum_attendance_id: this.itemToAttendance?.id,
        date: new Date().toISOString().split("T")[0],
      };
      return JSON.stringify(payload);
    },
    qr_am_out() {
      const payload = {
        type: "am_out",
        student_practicum_attendance_id: this.itemToAttendance?.id,
        org_id: null,
        date: new Date().toISOString().split("T")[0],
      };
      return JSON.stringify(payload);
    },
    qr_pm_in() {
      const payload = {
        type: "pm_in",
        student_practicum_attendance_id: this.itemToAttendance?.id,
        org_id: null,
        date: new Date().toISOString().split("T")[0],
      };
      return JSON.stringify(payload);
    },
    qr_pm_out() {
      const payload = {
        type: "pm_out",
        student_practicum_attendance_id: this.itemToAttendance?.id,
        org_id: null,
        date: new Date().toISOString().split("T")[0],
      };
      return JSON.stringify(payload);
    },
    qr_ot_in() {
      const payload = {
        type: "ot_in",
        student_practicum_attendance_id: this.itemToAttendance?.id,
        org_id: null,
        date: new Date().toISOString().split("T")[0],
      };
      return JSON.stringify(payload);
    },
    qr_ot_out() {
      const payload = {
        type: "ot_out",
        student_practicum_attendance_id: this.itemToAttendance?.id,
        org_id: null,
        date: new Date().toISOString().split("T")[0],
      };
      return JSON.stringify(payload);
    },
  },
  methods: {
    pagination(data) {
      this.paginationData = data;
    },
    initialize() {
      this.loading = true;
      setTimeout(() => {
        this.axiosCall(
          "/attendance/student-practicum-attendances/" + this.batch_selected,
          "GET",
        ).then((res) => {
          if (res) {
            this.data = res.data;
            this.loading = false;
          }
        });
      }, 500);
    },

    canBeEdited(item) {
      let currentDate = new Date();
      let itemDate = new Date(item.date_sched);
      if (itemDate < currentDate) {
        return false;
      }
      return true;
    },

    getTypeColor(type) {
      const palette = {
        orientation: { text: "#1d4ed8", bg: "#dbeafe" },
        training: { text: "#7c3aed", bg: "#ede9fe" },
        seminar: { text: "#ea580c", bg: "#ffedd5" },
        meeting: { text: "#0284c7", bg: "#e0f2fe" },
        evaluation: { text: "#db2777", bg: "#fce7f3" },
        exit_interview: { text: "#dc2626", bg: "#fee2e2" },
      };
      const key = (type || "").toLowerCase().replace(/\s+/g, "_");
      return palette[key] || { text: "#15803d", bg: "#dcfce7" };
    },

    getBatchList() {
      this.axiosCall("/student/get-batch-list", "GET").then((res) => {
        this.batchList = res.data;
        this.batch_selected = this.batchList[0].id;
      });
    },
    addnew() {
      this.updateData = { id: null, student_batch_id: this.batch_selected };
      this.action = "Add";
    },
    // deleteItem(item) {
    //   console.log(item);
    //   this.alerted = true;
    //   // this.dialogConfirmDelete = true;
    //   // this.deleteData = item;
    // },
    editItem(item) {
      this.updateData = item;
      this.action = "Update";
    },

    selectedSession() {
      this.qr_to_generate = this.list_selected == "Morning Session" ? 1 : 2;
    },

    showQRDialog(item) {
      console.log("item", item);
      //   this.qr_to_generate = item.type;
      this.itemToAttendance = item;
      this.show_qr_dialog = true;
    },

    closeQRDialog() {
      this.qr_to_generate = null;
      this.itemToAttendance = null;
      this.show_qr_dialog = false;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap");

.page-wrapper {
  font-family: "Plus Jakarta Sans", sans-serif;
  min-height: 100vh;
  color: #111827;
}

/* ── Header ── */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-title {
  font-size: 1.55rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 3px;
  letter-spacing: -0.02em;
}
.page-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.add-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  border-radius: 8px !important;
  padding: 0 20px !important;
  height: 40px !important;
  color: #fff !important;
}

/* ── Filter Bar ── */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 10px 16px;
  margin-bottom: 20px;
  max-width: 420px;
}
.filter-select {
  flex: 1;
}
.filter-select :deep(.v-field) {
  border-radius: 8px !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}
.filter-select :deep(.v-field__outline__start),
.filter-select :deep(.v-field__outline__end),
.filter-select :deep(.v-field__outline__notch) {
  border-color: transparent !important;
}

/* ── Content Card ── */
.content-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  overflow: hidden;
}

.card-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  gap: 16px;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.count-chip {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  padding: 1px 9px;
}

/* Search field */
.search-field {
  min-width: 220px;
}
.search-field :deep(.v-field) {
  border-radius: 8px !important;
  background: #f9fafb !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
}
.search-field :deep(.v-field__outline__start),
.search-field :deep(.v-field__outline__end),
.search-field :deep(.v-field__outline__notch) {
  border-color: #e5e7eb !important;
}
.search-field :deep(.v-field--focused .v-field__outline__start),
.search-field :deep(.v-field--focused .v-field__outline__end),
.search-field :deep(.v-field--focused .v-field__outline__notch) {
  border-color: #16a34a !important;
}
.search-field :deep(input) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #111827 !important;
}

/* ── Table ── */
.skills-table :deep(.v-data-table__th) {
  background: #f9fafb !important;
  color: #6b7280 !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #f3f4f6 !important;
  padding: 11px 20px !important;
}
.skills-table :deep(td) {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  color: #374151 !important;
  padding: 10px 20px !important;
  border-bottom: 1px solid #f9fafb !important;
}
.skills-table :deep(.v-data-table__tr:hover > td) {
  background: #f9fafb !important;
}

.cell-with-icon {
  display: flex;
  align-items: center;
  gap: 6px;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 3px 10px;
  border-radius: 20px;
}

.locked-label {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 600;
  color: #9ca3af;
}

/* Action buttons */
.action-btns {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.action-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  border-radius: 6px !important;
  height: 28px !important;
  padding: 0 10px !important;
  text-transform: uppercase !important;
}

.edit-btn {
  background: #dcfce7 !important;
  color: #15803d !important;
  border: 1px solid #bbf7d0 !important;
}
.edit-btn:hover {
  background: #bbf7d0 !important;
}

.qr-btn {
  background: #eff6ff !important;
  color: #1e40af !important;
  border: 1px solid #bfdbfe !important;
}
.qr-btn:hover {
  background: #dbeafe !important;
}

.delete-btn {
  background: #fee2e2 !important;
  color: #dc2626 !important;
  border: 1px solid #fecaca !important;
}
.delete-btn:hover {
  background: #fecaca !important;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 56px 20px;
  gap: 10px;
}
.empty-state p {
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
}

/* ── Confirm Dialog ── */
.confirm-dialog {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  border-radius: 14px !important;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 20px 16px;
}

.dialog-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dialog-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}
.dialog-sub {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 2px;
}

.dialog-body {
  padding: 16px 20px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

.dialog-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
}

.dialog-cancel-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  background: #f3f4f6 !important;
  color: #374151 !important;
  border: 1px solid #e5e7eb !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 36px !important;
  padding: 0 16px !important;
}

.dialog-confirm-btn {
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  background: #dc2626 !important;
  color: #fff !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  height: 36px !important;
  padding: 0 16px !important;
}

/* ── QR Dialog ── */
.qr-dialog-card {
  overflow: hidden;
}
.qr-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #16a34a;
  color: #fff;
  padding: 16px 20px;
}
.qr-dialog-title {
  font-size: 15px;
  font-weight: 700;
}
.qr-dialog-sub {
  font-size: 12px;
  opacity: 0.9;
  margin-top: 2px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
  .card-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
  .search-field {
    min-width: 100%;
    width: 100%;
  }
  .filter-bar {
    max-width: 100%;
  }
}
</style>
